import * as types from '@/store/mutation-types';
import api from '@/store/api';
import { logAction } from '../logActions';
import { editingAllowed } from '../actions';
import * as stageHelpers from '@/common/stageHelpers';
import router from '../../router';
import moment from 'moment';
import * as treeHelpers from '@/common/treeHelpers';
import { recalculatePartProperties } from '@/common/partHelpers';
import log from '@/logger';

export const lockProject = async ({ commit, state }, lock) => {
  if (lock && !state.currentProject.lockedForEditingBy) {
    await saveProjectProperties(
      { commit, state },
      {
        updateProject: state.currentProject,
        newProjectProperties: { lockedForEditingBy: state.user.username, lockedForEditingAt: moment().toISOString() },
      }
    );
    return;
  }
  var lockedAt = state.currentProject.lockedForEditingAt ? state.currentProject.lockedForEditingAt : '1900-01-01';

  if (
    !lock &&
    (state.currentProject.lockedForEditingBy === state.user.username || moment().diff(lockedAt, 'minute') > 60)
  ) {
    await saveProjectProperties(
      { commit, state },
      {
        updateProject: state.currentProject,
        newProjectProperties: { lockedForEditingBy: null, lockedForEditingAt: null },
      }
    );
  }
};

export const getDefaultProject = async () => {
  return api.prepareProjectEntity();
};

export const getStandardProject = async (_state, newProjectProperties) => {
  let standard = null;
  try {
    standard = await api.fetchProject('standard');
  } catch (error) {
    log.error('No standrad project exists!');
  }

  if (!standard) {
    standard = {
      projectSubcontractors: [],
      projectManagers: [],
    };
  }

  return api.prepareProjectEntity({
    projectSubcontractors: standard.projectSubcontractors,
    projectManagers: standard.projectManagers,
    ...(newProjectProperties || {}),
  });
};

export const createProject = ({ commit, state, dispatch }, newProjectProperties) => {
  log.debug('actions:createProject', newProjectProperties);

  commit(types.clearUndo);
  commit(types.setParts, {});
  commit(types.setStages, {});
  commit(types.setTodoTemplateList, []);

  let createdProject;
  return api
    .createProject(newProjectProperties)
    .then((project) => {
      createdProject = project;
      let personProjects = state.loggedInPerson.projects
        ? state.loggedInPerson.projects.concat([{ _id: project._id }])
        : [{ _id: project._id }];
      return dispatch('savePersonProperties', {
        updatePerson: state.loggedInPerson,
        newPersonProperties: { projects: personProjects },
      });
    })
    .then(async () => {
      commit(types.addProject, createdProject);
      var newProject = await dispatch('loadProject', createdProject._id);

      logAction({ commit, state }, { project: newProject, object: newProject, reason: 'Created Project' });
      return createdProject;
    })
    .catch((e) => {
      log.error('Error while creating project', e);
      commit(types.setSyncStatus, { status: 'Error', live: 0, message: 'Database Error occurred', count: 1 });
    });
};

export const undoSaveProjectProperties = async ({ commit, state, dispatch }, update) => {
  log.debug('actions:undoSaveProjectProperties', update);

  var undoProperties = update.newProjectProperties;
  for (var property in undoProperties) {
    undoProperties[property] = update.updateProject[property];
  }

  await dispatch('saveProjectProperties', {
    updateProject: state.currentProject,
    newProjectProperties: undoProperties,
    undoStrategy: 'undo',
  });
};

export const redoSaveProjectProperties = async ({ commit, state, dispatch }, update) => {
  log.debug('actions:redoSaveProjectProperties', update);
  await dispatch('saveProjectProperties', {
    updateProject: state.currentProject,
    newProjectProperties: update.newProjectProperties,
    undoStrategy: 'redo',
  });
};

export const saveProjectProperties = async ({ commit, state, dispatch }, update) => {
  log.debug('actions:saveProjectProperties', update);
  // console.log(JSON.stringify(update));
  var project;
  if (update.updateProject) {
    if (!update.updateProject._id) {
      update.updateProject = await dispatch('createProject', update.updateProject);
    }
    project = update.updateProject;
  } else {
    project = await api.fetchProject(update.projectId);
  }

  if (!editingAllowed(state, { newProjectProperties: update.newProjectProperties })) {
    return new Promise((resolve, reject) => {
      resolve(project);
    }).catch((e) => {
      log.error('projectActions:saveProjectProperties', e);
      commit(types.setSyncStatus, { status: 'Error', live: 0, message: 'Database Error occurred', count: 1 });
    });
  }

  update.updateProject = project;
  commit(types.appendUndo, { action: 'saveProjectProperties', data: update, undoStrategy: update.undoStrategy });
  let reason = logAction(
    { commit, state },
    { project: project, reason: 'Updated Project Properties', update: update.newProjectProperties, object: project }
  );
  commit(types.setProjectProperties, update);
  return api
    .saveProject(update.updateProject, reason)
    .then((savedProject) => {
      update.newProjectProperties = savedProject;

      commit(types.setProjectProperties, update);
      return update.updateProject;
    })
    .catch((e) => {
      log.error('projectActions:saveProjectProperties, Error saving project', e);
      commit(types.setSyncStatus, { status: 'Error', live: 0, message: 'Database Error occurred', count: 1 });
    });
};

export const syncProjectProperties = ({ commit, state }, update) => {
  commit(types.setProjectProperties, update);
};

export const setProjectProperties = ({ commit, state }, update) => {
  log.debug('actions:setProjectProperties', update.newProjectProperties);

  if (!editingAllowed(state)) {
    return;
  }

  commit(types.setProjectProperties, update);
};

export const updateProjectList = ({ commit, state }) => {
  return api
    .updateProjectList()
    .then((list) => {
      if (!list) {
        list = [];
      }
      commit(types.setProjectList, list);
    })
    .catch((e) => {
      log.error('projectActions:updateProjectList', e);
      commit(types.setSyncStatus, { status: 'Error', live: 0, message: 'Database Error occurred', count: 1 });
    });
};

export const addSyncedProject = ({ commit, state }, project) => {
  commit(types.addProject, project);
};

export const getProjectJson = async ({ commit, state, dispatch }, { project }) => {
  await api.loadDatabase(project._id, state, dispatch);
  var pParts = api.getParts(project._id);
  var pStages = api.getStages(project._id);
  var pProject = api.fetchProject(project._id);

  return Promise.all([pProject, pStages, pParts])
    .then(([project, stages, parts]) => {
      return JSON.stringify({ project, stages, parts }, null, '\t');
    })
    .catch((e) => {
      log.error('projectActions:getProjectJSon', e);
      commit(types.setSyncStatus, { status: 'Error', live: 0, message: 'Database Error occurred', count: 1 });
    });
};

export const duplicateProject = async ({ commit, state, dispatch }, { project }) => {
  await api.loadDatabase(project._id, state, dispatch);
  var pParts = api.getParts(project._id);
  var pStages = api.getStages(project._id);
  var pProject = api.fetchProject(project._id);

  var newProject = await Promise.all([pProject, pStages, pParts])
    .then(([project, stages, parts]) => {
      return dispatch('importProject', { projectData: { project, stages, parts } });
    })
    .catch((e) => {
      log.error('projectActions:duplicateProject', e);
      commit(types.setSyncStatus, { status: 'Error', live: 0, message: 'Database Error occurred', count: 1 });
    });

  newProject = await dispatch('loadProject', newProject._id);
  addSyncedProject({ commit, state }, newProject);
  logAction({ commit, state }, { project: newProject, object: project, reason: 'Duplicated Project' });

  return newProject;
};

export const copyChildren = async ({ commit, state, dispatch }, { fromProjectId, toProjectId }) => {
  await api.loadDatabase(fromProjectId, state, dispatch);
  await api.switchDatabase(toProjectId, state, dispatch);
  let pParts = api.getParts(fromProjectId);
  let pStages = api.getStages(fromProjectId);
  var pProject = api.fetchProject(fromProjectId);
  let pTodoTemplates = dispatch('getTodoTemplates', { projectId: fromProjectId });
  let pInspectionTemplates = dispatch('getInspectionTemplates', { projectId: fromProjectId });

  return Promise.all([pProject, pStages, pParts, pTodoTemplates, pInspectionTemplates])
    .then(([project, stages, parts, todoTemplates, inspectionTemplates]) => {
      return api.copyChildren({
        projectData: { project, stages, parts, todoTemplates, inspectionTemplates },
        newProjectId: toProjectId,
      });
    })
    .then(async () => {
      commit(types.setProject, {});
      return dispatch('loadProject', toProjectId);
    })
    .catch((e) => {
      log.error('projectActions:copyChildren', e);
      commit(types.setSyncStatus, { status: 'Error', live: 0, message: 'Database Error occurred', count: 1 });
    });
};

export const importProject = async ({ commit, state, dispatch }, { projectData }) => {
  var newProject = await api.importProject({ dispatch, state, projectData });

  await dispatch('savePersonProperties', {
    updatePerson: state.loggedInPerson,
    newPersonProperties: { projects: state.loggedInPerson.projects.concat([{ _id: newProject._id }]) },
  });

  commit(types.addProject, newProject);
  newProject = await dispatch('loadProject', newProject._id);
  addSyncedProject({ commit, state }, newProject);
  logAction({ commit, state }, { project: newProject, object: newProject, reason: 'Created Project' });

  return newProject;
};

export const loadProject = async ({ commit, state, dispatch, getters }, _id) => {
  if (_id === state.currentProject._id) {
    return state.currentProject;
  }
  commit(types.setIsBusy, 1);
  commit(types.setSyncStatus, { status: 'Active', live: 0, message: 'Loading Project', count: 1 });

  commit(types.clearUndo);
  commit(types.setProject, {});
  commit(types.setParts, {});
  commit(types.setStages, {});
  commit(types.setTodoTemplateList, []);
  commit(types.setDiaryEntries, []);

  if (!getters.projectIds.includes(_id) && _id !== 'standard') {
    commit(types.setIsBusy, -1);
    router.push({ name: 'ProjectList' });
    return;
  }

  await api.switchDatabase(_id, state, dispatch);

  var parts = await api.getParts(_id);
  commit(types.setParts, parts);

  var stages = await api.getStages(_id);
  commit(types.setStages, stages);

  var project = await api.fetchProject(_id).catch((e) => {
    commit(types.setSyncStatus, { status: 'Active', live: 1, message: '', count: 1 });
    router.push({ name: 'ProjectList' });
    log.error('projectActions:loadProject', e);
    commit(types.setIsBusy, -1);
    throw new Error('Unable to load project');
  });
  commit(types.setProject, project);
  await dispatch('loadProjectTodoTemplates', { projectId: _id });
  await dispatch('loadProjectInspectionTemplates', { projectId: _id });
  if (getters.hasSimplePermissions('loadProjectDiaryEntries')) {
    await dispatch('loadProjectDiaryEntries', { projectId: _id });
  }

  commit(types.setSyncStatus, { status: 'Active', live: 1, message: '', count: 1 });
  commit(types.setIsBusy, -1);
  return project;
};

export const addProjectUserItem = ({ commit, state }, addInfo) => {
  commit(types.addProjectUserItem, addInfo);
};

export const updateProjectUserItem = ({ commit, state, dispatch }, update) => {
  console.debug('updateProjectUserItem', update);
  if (update.property === 'companyId' && update.newValue !== update.userItem.companyId) {
    commit(types.updateProjectUserItem, { userItem: update.userItem, property: 'personId', newValue: '' });
  }

  if (update.property === 'companyId') {
    var prevCompany = state.companies[update.userItem.companyId];
    var newCompany = state.companies[update.newValue];
    logAction(
      { commit, state },
      {
        project: update.project,
        object: {
          title: update.userItem.occupation,
          company: prevCompany ? prevCompany.companyName : '',
        },
        reason: 'Changed Role',
        update: {
          company: newCompany ? newCompany.companyName : '',
        },
      }
    );
  } else {
    var prevPerson = state.persons[update.userItem.personId];
    var newPerson = state.persons[update.newValue];
    logAction(
      { commit, state },
      {
        project: update.project,
        object: {
          title: update.userItem.occupation,
          person: prevPerson ? prevPerson.firstName + ' ' + prevPerson.lastName : '',
        },
        reason: 'Changed Role',
        update: {
          person: newPerson ? newPerson.firstName + ' ' + newPerson.lastName : '',
        },
      }
    );
  }

  commit(types.updateProjectUserItem, update);
  return dispatch('saveProjectProperties', { updateProject: update.project, newProjectProperties: {} });
};

export const undoProjectChildMoved = (
  { commit, state, dispatch },
  { oldParent, oldIndex, newParent, newIndex, movedChild }
) => {
  var project = state.currentProject;

  newParent = treeHelpers.findChildInTree(project, oldParent._id).children;
  oldParent = treeHelpers.findParent(project, movedChild._id).children;

  return performMove(commit, state, oldParent, newIndex, newParent, oldIndex, movedChild, project);
};

export const redoProjectChildMoved = (
  { commit, state, dispatch },
  { oldParent, oldIndex, newParent, newIndex, movedChild }
) => {
  var project = state.currentProject;

  newParent = treeHelpers.findChildInTree(project, newParent._id).children;
  oldParent = treeHelpers.findParent(project, movedChild._id).children;

  return performMove(commit, state, oldParent, oldIndex, newParent, newIndex, movedChild, project);
};

export const projectChildMoved = ({ commit, state }, { movedChild, parent, beforeChild, undoStrategy }) => {
  var project = state.currentProject;

  // treeHelpers.outputTree(store, project.children, 0);

  var oldParent = treeHelpers.findParent(project, movedChild._id);
  var newParent = treeHelpers.findChildInTree(project, parent._id);
  var oldIndex = treeHelpers.indexOfChild(oldParent.children, movedChild._id);
  var newIndex = beforeChild
    ? treeHelpers.indexOfChild(newParent.children, beforeChild._id)
    : newParent.children.length;

  if (oldParent._id === newParent._id && oldIndex === newIndex - 1) {
    return;
  }

  if (oldParent._id === newParent._id && oldIndex < newIndex) {
    newIndex--;
  }

  commit(types.appendUndo, {
    action: 'projectChildMoved',
    data: { oldParent, oldIndex, newParent, newIndex, movedChild },
    undoStrategy: undoStrategy,
  });
  return performMove(commit, state, oldParent.children, oldIndex, newParent.children, newIndex, movedChild, project);
};

function performMove(commit, state, oldParent, oldIndex, newParent, newIndex, movedChild, project) {
  if (movedChild.type === 'Stage') {
    commit(types.deleteStage, { parent: oldParent, index: oldIndex });
    commit(types.addStage, { parent: newParent, newStage: movedChild, index: newIndex });
  } else {
    commit(types.deletePart, { parent: oldParent, index: oldIndex });
    commit(types.addPart, { parent: newParent, newPart: movedChild, index: newIndex });
  }

  // treeHelpers.outputTree(store, project.children, 0);
  logAction({ commit, state }, { project: project, object: movedChild, reason: 'Moved Child' });

  return api
    .saveProject(project)
    .then((savedProject) => {
      commit(types.setProjectProperties, { updateProject: project, newProjectProperties: savedProject });

      return savedProject;
    })
    .catch((e) => {
      log.error('projectActions:performMove', e);
      commit(types.setSyncStatus, { status: 'Error', live: 0, message: 'Database Error occurred', count: 1 });
    });
}

export const pasteChildNode = async ({ dispatch, commit, state }, { pasteOnNode, pasteNode, startdate }) => {
  commit(types.setSyncStatus, { status: 'Info', live: 1, message: 'Pasting', count: 1 });
  commit(types.setIsBusy, 1);

  var parent = treeHelpers.findParent(state.currentProject, pasteOnNode._id);
  var index = treeHelpers.indexOfChild(parent.children, pasteOnNode._id);

  if (pasteNode.type === 'Part') {
    var update = { updatePart: pasteNode, newPartProperties: { startdate } };
    recalculatePartProperties(update);
    pasteNode.startdate = update.newPartProperties.startdate;
    pasteNode.expectedDonedate = update.newPartProperties.expectedDonedate;
    pasteNode.controldate = update.newPartProperties.controldate;
    pasteNode.calendarTime = update.newPartProperties.calendarTime;
    pasteNode.deliverydate = '0000-01-01';
    pasteNode.text = '';
    pasteNode.controlFulfilled = false;

    var newPart = await api.createPart(state.currentProject._id, pasteNode);
    commit(types.addPart, { newPart, parent: parent.children, index: index });
  } else {
    var firstStartdate = stageHelpers.calculateStartdateFromTree(pasteNode);
    if (firstStartdate === '9999-12-31') {
      firstStartdate = startdate;
    }
    var adjustDays = -moment(firstStartdate).diff(startdate, 'days');
    var newTree = await pasteChildNodeRecurse(commit, state, pasteNode, adjustDays);
    commit(types.addStage, { newStage: newTree, parent: parent.children, index: index });
  }

  await api.saveProject(state.currentProject).then((savedProject) => {
    commit(types.setProjectProperties, { updateProject: state.currentProject, newProjectProperties: savedProject });
  });
  await dispatch('clearUndo');
  commit(types.setIsBusy, -1);
};

const pasteChildNodeRecurse = async function (commit, state, pasteNode, adjustDays) {
  var newStage = await api.createStage(state.currentProject._id, Object.assign({}, pasteNode, { children: undefined }));
  commit(types.addSyncedStage, newStage);

  var newSubTree = { _id: newStage._id, title: newStage.title, type: 'Stage', children: [] };

  for (var child of pasteNode.children) {
    if (child.type === 'Stage') {
      var newTree = await pasteChildNodeRecurse(commit, state, child, adjustDays);
      newSubTree.children.push(newTree);
    } else if (child.type === 'Part') {
      var update = {
        updatePart: child,
        newPartProperties: { startdate: moment(child.startdate).add(adjustDays, 'days').format('YYYY-MM-DD') },
      };
      recalculatePartProperties(update);
      child.startdate = update.newPartProperties.startdate;
      child.expectedDonedate = update.newPartProperties.expectedDonedate;
      child.controldate = update.newPartProperties.controldate;
      child.calendarTime = update.newPartProperties.calendarTime;

      var newPart = await api.createPart(state.currentProject._id, child);
      commit(types.addSyncedPart, newPart);
      newSubTree.children.push({ _id: newPart._id, type: 'Part', title: newPart.title });
    }
  }

  return newSubTree;
};

export const copyChildNode = ({ commit, state }, { node }) => {
  var firstChild = treeHelpers.findChildInTree(state.currentProject, node._id);
  return buildCopyTree(state, state.currentProject, firstChild);
};

const buildCopyTree = function (state, project, node) {
  if (node.type === 'Part') {
    return Object.assign({}, state.parts[node._id], {
      originalId: node._id,
      originalProjectId: project._id,
      projectId: undefined,
      _id: undefined,
      _rev: undefined,
    });
  }

  var childStage = Object.assign({}, state.stages[node._id], {
    originalId: node._id,
    originalProjectId: project._id,
    projectId: undefined,
    _id: undefined,
    _rev: undefined,
    children: [],
  });
  for (var child of node.children) {
    childStage.children.push(buildCopyTree(state, project, child));
  }
  return childStage;
};

export const getProjectLog = async ({ commit }, { projectId }) => {
  commit(types.setIsBusy, 1);
  try {
    return await api.getProjectLog({ projectId });
  } catch (error) {
    log.error('projectActions:getProjectLog', error);
    commit(types.setSyncStatus, { status: 'Error', live: 0, message: 'Database Error occurred ' + error, count: 1 });
  } finally {
    commit(types.setIsBusy, -1);
  }
};

export const getProjectItem = ({ commit }, id) => {
  return api
    .getProjectItem(id)
    .then((object) => {
      return Promise.resolve(object);
    })
    .catch((e) => {
      log.error('projectActions:getProjectItem', e);
      commit(types.setSyncStatus, { status: 'Error', live: 0, message: 'Database Error occurred ' + e, count: 1 });
    });
};

export const addProjectUserItemByIndex = ({ commit, state, dispatch }, addInfo) => {
  // add new occupation then update state currentProject
  commit(types.addProjectUserItemByIndex, addInfo);
  // return dispatch('saveProjectProperties', {updateProject: state.currentProject, newProjectProperties: {}});
};

export const updateProjectUserItemOccupation = ({ commit, state, dispatch }, updateInfo) => {
  // occupation change to update state and DB
  commit(types.updateProjectUserItemOccupation, updateInfo);
  return dispatch('saveProjectProperties', { updateProject: state.currentProject, newProjectProperties: {} });
};

export const deleteProjectUserItem = ({ commit, state, dispatch }, deleteInfo) => {
  // delete occupation from state and DB
  commit(types.deleteProjectUserItem, deleteInfo);
  return dispatch('saveProjectProperties', { updateProject: state.currentProject, newProjectProperties: {} });
};



// WEBPACK FOOTER //
// ./src/store/project/projectActions.js